import {
  getPrimaryEnrolledProgram,
  readSelfLearnSelectedProgramIdFromStorage,
} from "@repo/self-learn-web";
import type { EnrolledProgram } from "../store/useAuthStore";

export const SELF_LEARN_SELECTED_PROGRAM_KEY = "SELF_LEARN_SELECTED_PROGRAM";

/** Prevents re-entrant localStorage writes while syncing from enrollment. */
let isSyncingSelfLearnSelectedProgram = false;

/**
 * Align `SELF_LEARN_SELECTED_PROGRAM` with enrollment primary when there is no valid stored choice.
 * Only touches localStorage (never auth store) to avoid update loops.
 */
export function syncSelfLearnSelectedProgramFromEnrollment(
  enrolledPrograms: EnrolledProgram[] | null | undefined,
): void {
  if (typeof window === "undefined" || isSyncingSelfLearnSelectedProgram) {
    return;
  }

  const target = getPrimaryEnrolledProgram(enrolledPrograms);
  if (!target?.program_id) {
    clearSelfLearnSelectedProgramStorage();
    return;
  }

  const storedId = readSelfLearnSelectedProgramIdFromStorage();
  if (
    storedId &&
    storedId !== target.program_id &&
    enrolledPrograms?.some((p) => p.program_id === storedId)
  ) {
    return;
  }
  if (storedId === target.program_id) {
    return;
  }

  try {
    isSyncingSelfLearnSelectedProgram = true;
    localStorage.setItem(
      SELF_LEARN_SELECTED_PROGRAM_KEY,
      JSON.stringify(target),
    );
  } catch {
    // localStorage unavailable — non-fatal
  } finally {
    isSyncingSelfLearnSelectedProgram = false;
  }
}

/** Clears persisted self-learn program selection (e.g. on logout). */
export function clearSelfLearnSelectedProgramStorage(): void {
  if (typeof window === "undefined" || isSyncingSelfLearnSelectedProgram) {
    return;
  }

  try {
    if (!localStorage.getItem(SELF_LEARN_SELECTED_PROGRAM_KEY)) return;
    isSyncingSelfLearnSelectedProgram = true;
    localStorage.removeItem(SELF_LEARN_SELECTED_PROGRAM_KEY);
  } catch {
    // non-fatal
  } finally {
    isSyncingSelfLearnSelectedProgram = false;
  }
}
